import * as Sentry from '@sentry/react';
import { keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import i18n from 'i18next';
import {
  type IObservableArray,
  makeAutoObservable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';

import type {
  CartBundlesCalculationItemRequest,
  CartCalculationItemRequest,
  CartCalculationItemResponse,
  CartCalculationOrderResponse,
  CartCalculationRequest,
  CartCalculationResponse,
} from '~/api/Catalog';
import { CatalogRequests, ProductCommodityGroupAPI } from '~/api/Catalog';
import { DeliveryMechanic, type DeliveryMethods } from '~/api/ETA';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { company } from '~/company/Company';
import { getProductLimit } from '~/hooks/useProductLimit';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { settingStore } from '~/stores';
import type { Offer, Product } from '~/stores/CategoriesStore';
import { LEGACY_TO_NEW_DELIVERY_METHOD_NAME } from '~/stores/DeliveryStore/constants';
import { DeliveryServiceVersion } from '~/stores/DeliveryStore/interfaces';
import type { CalculateStockErrorInstance } from '~/stores/shared/CartStockChanges/interfaces';
import { calcProductOfferFields, isProductBundle } from '~/types/Product';
import {
  getApplicableMultibuyLevel,
  sortMultibuyLevels,
} from '~/utils/multibuy';
import type { CheckoutStore } from '../CheckoutStore/CheckoutStore';
import { storage as localStorage } from '../LocalStorage';
import type { MainStore } from '../MainStore';
import type { OrderStore } from '../OrderStore/OrderStore';
import { PromotionStore } from '../PromotionsStore';
import { CartStockChanges } from '../shared/CartStockChanges';
import { Query } from '../shared/Query';
import type { UserStore } from '../UserStore';
import type {
  CalculationProcess,
  CartItem,
  CartItemBundle,
  CartItemOffer,
  FirebaseCartItem,
  ICartStore,
  Promocode,
  TotalCartPrice,
} from './interfaces';
import { cartQueryKeys } from './queryKeys';

export class CartStore implements ICartStore {
  private _isCalculateEnabled = false;

  cart: CartItem[] = [];

  totalCartPrice: TotalCartPrice = {
    amountLeftToDiscount: '',
    base: '',
    discount: '',
    paid: '',
    paidWithDiscount: '',
    promocodeDiscount: '',
    loyaltyPointsDiscount: '',
    paidBonusesPence: 0,
    paidLoyaltyPointsPence: 0,
    paidWithoutDiscount: '',
    taxAmount: 0,
    taxPercent: 0,
    minimal_order_value_error: undefined,
  };

  deliveriesOrders: CartCalculationOrderResponse[] = [];

  addToCartCatch: {
    cartItem: Offer | Product;
    count: number;
    source: string;
  } | null = null;

  changePriceProductList: CartItem[] = [];
  changeCountProductList: CartItem[] = [];
  productsOutOfStockExpandedList: string[] = [];
  isMissingBirthdayErrorCatched: boolean = false;

  readonly cartStockChangesStore: CartStockChanges;
  readonly promotionStore: PromotionStore;

  private declare readonly calculateQuery: Query<CartCalculationResponse>;

  constructor(
    private readonly userStore: UserStore,
    private readonly orderStore: OrderStore,
    private readonly checkoutStore: CheckoutStore,
    private readonly mainStore: MainStore,
  ) {
    this.promotionStore = new PromotionStore(this, userStore);
    this.cartStockChangesStore = new CartStockChanges(this);

    this.migrateFromCatalogStore();

    makeAutoObservable(this);

    // Instantiate Query AFTER makeAutoObservable so that _isCalculateEnabled,
    // cart, and other own-state observables are reactive when the Query sets
    // up its internal reaction. Otherwise the reaction captures their initial
    // values as plain props and subsequent mutations don't re-trigger it.
    // The `declare` modifier keeps this property invisible to makeAutoObservable.
    (
      this as unknown as { calculateQuery: Query<CartCalculationResponse> }
    ).calculateQuery = new Query<CartCalculationResponse>(undefined, () => ({
      queryKey: cartQueryKeys.calculate(this.cartHash, this.calculateDepsHash),
      queryFn: () => this.executeCalculate(),
      enabled: this._isCalculateEnabled && this.cart.length > 0,
      staleTime: 0,
      gcTime: 5 * 60_000,
      placeholderData: keepPreviousData,
      retry: 1,
      // Coalesce the two-phase cart→deliveries update into one refetch.
      // A cart mutation changes cartHash immediately, then the reaction in
      // stores/index.ts runs checkoutStore.calculateDeliveries() which
      // updates deliveries.itemsIds (tracked via calculateDepsHash). Without
      // a delay the query fires twice. 50ms is enough for the follow-up
      // reaction to settle and is imperceptible to the user.
      dynamicOptionsUpdateDelay: 50,
    }));

    reaction(
      () => this.cart.some(({ count }) => count <= 0),
      (hasZeroCount) => {
        if (hasZeroCount) {
          this.cart = this.cart.filter(({ count }) => count > 0);
        }
      },
    );

    reaction(
      () => ({
        data: this.calculateQuery.result.data,
        isPlaceholder: this.calculateQuery.result.isPlaceholderData,
      }),
      ({ data, isPlaceholder }) => {
        if (data && !isPlaceholder) {
          runInAction(() => {
            this.isMissingBirthdayErrorCatched = false;
            this.processCalculateResponse(data);
          });
        }
      },
    );

    makePersistable(this, {
      name: 'CartStore',
      properties: ['cart', 'totalCartPrice'],
      storage: localStorage,
    }).catch((error) => error && console.error(error));
  }

  // Computed getters

  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  get localTotal(): number {
    let total = 0;

    for (const item of this.selectedCartItems) {
      total += this.getEffectiveItemPrice(item) * item.count;
    }

    return total;
  }

  get totalCartCount(): number {
    let totalCount = 0;
    for (let i = 0; i < this.cart.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      totalCount += this.cart[i].count;
    }
    return totalCount;
  }

  get selectedCartItems() {
    return this.cart.filter(({ selected }) => selected);
  }

  get isSelectedAll() {
    return this.cart.every(({ selected }) => selected);
  }

  get selectedItemsCount() {
    return this.cart.filter(({ selected }) => selected).length;
  }

  get selectedItemsTotalCount() {
    return this.selectedCartItems.reduce<number>(
      (total, { count }) => total + count,
      0,
    );
  }

  get finalPrice() {
    return this.totalCartPrice.paid || '0';
  }

  get cartForFirebase(): FirebaseCartItem[] {
    return this.cart.map((item, i): FirebaseCartItem => {
      return this.convertToFirebase(item, i);
    });
  }

  get isRestrictedItemInCart(): boolean {
    const commodityGroupList: ProductCommodityGroupAPI[] = [
      ProductCommodityGroupAPI.TOBACCO,
      ProductCommodityGroupAPI.ALCOHOL,
    ];

    for (let i = 0; i < this.cart.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      if (commodityGroupList.includes(this.cart[i].commodityGroup)) {
        return true;
      }
    }

    return false;
  }

  get hasRestrictedCategoriesInCart(): boolean {
    const restrictedCategories = settingStore.restrictedCategories;

    if (!restrictedCategories.length) {
      return false;
    }

    const disallowedCommodityGroups = settingStore.restrictedCategories.map(
      (item) => item.name,
    );
    const commodityGroups = this.selectedCartItems
      .map((item) => item.commodityGroup?.name)
      .filter(Boolean) as string[];

    if (
      disallowedCommodityGroups.some((category) =>
        commodityGroups.includes(`${category}`),
      )
    ) {
      return true;
    }

    return false;
  }

  get isAdultItemInCart(): boolean {
    return this.cart.some((item) => item.properties.age_restriction);
  }

  get isAdultCommodityInCart(): boolean {
    return this.cart.some((item) => {
      const commodityGroupId = item.commodityGroup?.id;

      return commodityGroupId === 2 || commodityGroupId === 3;
    });
  }

  get leftUntilFreeDelivery(): [string, string] {
    const zero: [string, string] = ['0', '100%'];
    if (this.userStore.personalData.freeDeliveryDaysLeft > 0) {
      return zero;
    }

    return this.leftUntilFreeFee(
      this.mainStore.convertPoundsToPence(this.totalCartPrice.paidWithDiscount),
      this.orderStore.fee.threshold || 0,
    );
  }

  get leftUntilFreeMinimalOrderFee(): [string, string] {
    const zero: [string, string] = ['0', '100%'];
    if (this.userStore.isStaff) {
      return zero;
    }

    if (this.orderStore.isFirstOrder) {
      return zero;
    }

    const paid = this.mainStore.convertPoundsToPence(
      this.totalCartPrice.paidWithDiscount,
    );

    return this.leftUntilFreeFee(paid, company.config.minimalOrderFeeThreshold);
  }

  get isStoreInMinimalOrderList() {
    return company.config.warehouse.minimalOrderFeeWarehouseCodes.includes(
      this.orderStore.etaWarehouseCode,
    );
  }

  get isMinimalOrderFeePassed() {
    if (this.userStore.isStaff) {
      return true;
    }

    if (this.orderStore.isFirstOrder) {
      return true;
    }

    if (!this.isStoreInMinimalOrderList) {
      return true;
    }

    if (this.cart.length === 0) {
      return false;
    }

    return this.mainStore.isZero(this.leftUntilFreeMinimalOrderFee[0]);
  }

  get isPromoCodeApplied() {
    return (
      this.promocode.success &&
      parseFloat(this.totalCartPrice.promocodeDiscount) > 0 &&
      this.mainStore.isZero(this.totalCartPrice.amountLeftToDiscount)
    );
  }

  get isFixedPromoCode() {
    return this.promocode.coupon?.type === 'FIXED';
  }

  get formatPromocodeDiscountAmount() {
    const amount = this.promocode.coupon?.value || 0;

    if (this.mainStore.isZero(amount)) {
      return '0';
    }

    if (this.promocode.coupon?.type === 'PERCENTAGE') {
      return `${amount}%`;
    }

    return this.mainStore.addCurrencySymbol(
      this.mainStore.convertPenceToPounds(amount),
    );
  }

  get promocode(): Promocode {
    return this.promotionStore.promocode;
  }

  // Methods

  setCartItemCountByProduct(
    cartItem: Offer | Product,
    count: number,
    action: 'add' | 'remove',
    source: string,
    lvl3category?: number,
  ) {
    const isBundle = isProductBundle(cartItem);

    analyticsService.track(AnalyticsEvent.CART_CHANGED, {
      cart_id: undefined,
      type: 'stock',
      products_amount: this.cart.reduce((sum, item) => sum + item.count, 0),
      items_amount: this.cart.length,
      price: this.totalCartPrice.base,
      final_price: this.finalPrice,
      eta_min: this.orderStore.etaCalculation?.duration.min || 0,
      eta_max: this.orderStore.etaCalculation?.duration.max || 0,
      delivery_fee: this.orderStore.fee.shippingPounds || 0,
      threshold: this.orderStore.fee.thresholdPounds || 0,
      is_surger: this.orderStore.etaCalculation?.highDemand || false,
      stage: 'checkout',
    });
    if (count < 0) {
      count = 0;
    }

    if (
      cartItem.properties.age_restriction &&
      !this.userStore.personalData.isAdult &&
      action === 'add' &&
      count
    ) {
      this.addToCartCatch = { cartItem, count, source };
      this.mainStore.setIsAgeRestrictionPopover(true);
      return;
    }

    const itemState = [];
    const { currentWhSellable, laterCount } = getProductLimit(cartItem);
    const isExpress =
      currentWhSellable && this.orderStore.isExpressAvailableNow;

    if (action === 'add' && currentWhSellable + 1 === count && isExpress) {
      itemState.push(
        i18n.t('expressAvailable', {
          expressItems: currentWhSellable,
          standardItems: laterCount,
        }),
      );
    }

    if (action === 'add' && !isBundle && cartItem.multibuyPriceLevels?.length) {
      const sortedLevels = sortMultibuyLevels(cartItem.multibuyPriceLevels);

      const reachedLevel = sortedLevels.find(
        (level) => count === level.quantity,
      );

      if (reachedLevel) {
        itemState.push(i18n.t('phrases:discountReached'));
      } else {
        const nextLevel = sortedLevels.find((level) => count < level.quantity);

        if (nextLevel) {
          itemState.push(
            i18n.t('phrases:addMoreToDiscount', {
              count: nextLevel.quantity - count,
            }),
          );
        }
      }
    }

    if (itemState.length) {
      const message = itemState.join('<br /><br/>');

      this.mainStore.popAlert();
      this.mainStore.pushAlert('success', message);
    }

    const itemIndex = this.cart.findIndex(({ id, sku }) =>
      isBundle ? id && id === cartItem.id : sku && sku === cartItem.sku,
    );
    const isNewProduct = itemIndex === -1;
    if (!isNewProduct) {
      if (!count) {
        this.cart.splice(itemIndex, 1);

        if (!this.cart.length) {
          this.promotionStore.resetPromocode();
        }
      } else {
        const item = this.cart[itemIndex];

        if (!item) {
          return;
        }

        this.cart[itemIndex] = { ...item, count };
        (this.cart as IObservableArray<CartItem>).replace(this.cart);
      }
    }

    if (count && isNewProduct) {
      this.cart.push({
        ...cartItem,
        count,
        selected: true,
        sku: isBundle ? `${cartItem.id}` : cartItem.sku,
      });
      this.mainStore.sendToRN('sendTags', {
        cart_product_name: cartItem.name,
      });
      this.mainStore.sendToRN('sendTags', {
        cart_product_price: cartItem.discountPrice
          ? cartItem.discountPriceFormatted
          : cartItem.priceFormatted,
      });
      this.mainStore.sendToRN('sendTags', {
        cart_product_image: cartItem.previewImageThumb,
      });
    }

    this.mainStore.sendToRN('sendTags', {
      cart_update_time: Math.floor(Date.now() / 1000),
    });

    this.cartStockChangesStore.closeNotifierModal();

    this.trackCartItemChange({
      cartItem,
      count,
      action,
      source,
      isBundle,
      lvl3category,
    });
  }

  emptyCart() {
    this.cart = [];
    this.promotionStore.resetPromocode();
    this.mainStore.sendToRN('removeTag', 'cart_update_time');
    this.mainStore.sendToRN('removeTag', 'cart_product_name');
    this.mainStore.sendToRN('removeTag', 'cart_product_price');
    this.mainStore.sendToRN('removeTag', 'cart_product_image');
    this.calculateQuery.reset();
  }

  removeCartItemByOfferSKU(skuToRemove: string) {
    this.cart = this.cart.filter(({ sku }) => sku !== skuToRemove);
  }

  removeCartItemByOfferId(offerId: number) {
    this.cart = this.cart.filter(({ id }) => id !== offerId);
  }

  emptySelectedItemsFromCart() {
    if (this.selectedItemsCount === 0) {
      return;
    }
    if (this.cart.length === this.selectedItemsCount) {
      this.cart = [];
    } else {
      this.cart = this.cart.reduce((acc: CartItem[], item) => {
        if (!item.selected) {
          acc.push({ ...item, selected: true });
        }
        return acc;
      }, []);
    }
  }

  handleSelectedCartItem = (sku: string) => {
    const currentItemIndex = this.cart.map((i) => i.sku).indexOf(sku);
    const currentItem = this.cart[currentItemIndex];

    if (!currentItem) {
      return;
    }

    this.cart[currentItemIndex] = {
      ...currentItem,
      selected: !currentItem.selected,
    };
  };

  handleSelectAllCartItems = () => {
    this.cart = this.cart.map((item) => ({
      ...item,
      selected: !this.isSelectedAll,
    }));
  };

  getCartItemCountById(item: Offer | Product): number {
    if (!item) {
      return 0;
    }

    const id = item.id;
    const sku = item.sku;
    const isBundle = isProductBundle(item);
    const cartItem = this.cart.find((item) => {
      const typeIsEqual = isBundle
        ? (item as Product).productType === 'bundle'
        : (item as Product).productType !== 'bundle';
      const idIsEqual = isBundle
        ? (item.id && item.id === id) || (item.sku && item.sku === sku)
        : item.sku && item.sku === sku;

      return idIsEqual && typeIsEqual;
    });

    return cartItem ? cartItem.count : 0;
  }

  get calculationProcess(): CalculationProcess {
    return {
      isLoading: this.calculateQuery.isFetching,
      isError: this.calculateQuery.isError,
    };
  }

  get isCalculating(): boolean {
    return this.calculateQuery.isFetching;
  }

  get isPlaceholderData(): boolean {
    return this.calculateQuery.isPlaceholderData;
  }

  get isCalculateError(): boolean {
    return this.calculateQuery.isError;
  }

  async enableCalculate(): Promise<void> {
    await this.orderStore.reCalculateDeliveries();
    this._isCalculateEnabled = true;
  }

  disableCalculate(): void {
    this._isCalculateEnabled = false;
  }

  async calculateCart(): Promise<boolean> {
    if (!this.cart.length) return false;
    try {
      await this.calculateQuery.refetch();
      return (
        this.changePriceProductList.length > 0 ||
        this.changeCountProductList.length > 0
      );
    } catch {
      return false;
    }
  }

  destroy() {
    this.cartStockChangesStore.destroy();
    this.calculateQuery.destroy();
  }

  convertToFirebase(
    item: (Product | Offer) & { count?: number },
    i: number = 0,
  ): FirebaseCartItem {
    return {
      item_id: isProductBundle(item)
        ? item.id.toString()
        : (item as Offer).sku || item.id.toString(),
      item_name: item.name,
      quantity: item.count || 1,
      promotion_id: '',
      promotion_name: '',
      affiliation: '',
      coupon: this.promocode.value,
      creative_name: '',
      creative_slot: '',
      discount: item.discountPrice
        ? this.mainStore.toFloat(
            this.mainStore.convertPenceToPounds(
              item.price - item.discountPrice,
            ),
          )
        : 0,
      index: i,
      item_brand: '',
      item_category: item.categoryName || '',
      item_list_name: item.categoryName || '',
      item_list_id: item.categoryId?.toString() || '',
      item_variant: '',
      location_id: '',
      tax: 0,
      price: this.mainStore.toFloat(item.priceFormatted),
      currency: this.orderStore.currency.toUpperCase(),
    };
  }

  // Private methods

  private trackCartItemChange(params: {
    cartItem: Offer | Product;
    count: number;
    action: 'add' | 'remove';
    source: string;
    isBundle: boolean;
    lvl3category?: number;
  }): void {
    const { cartItem, count, action, source, isBundle, lvl3category } = params;

    const analyticEvent =
      action === 'add'
        ? AnalyticsEvent.PURCHASE_PRODUCT_ADDED_TO_CART
        : AnalyticsEvent.PURCHASE_PRODUCT_REMOVED_FROM_CART;
    const productsAmount = this.cart.reduce((sum, item) => sum + item.count, 0);

    analyticsService.track(analyticEvent, {
      product_id: cartItem.id,
      category_id: lvl3category || cartItem.categoryId,
      lvl1_category_id: undefined,
      lvl2_category_id: cartItem.parentCategoryId,
      source,
      quantity: count,
      cart_id: undefined,
      products_amount: productsAmount,
      items_amount: this.cart.length,
      price: this.totalCartPrice.base,
      final_price: this.finalPrice,
      eta_min: this.orderStore.etaCalculation?.duration.min || 0,
      eta_max: this.orderStore.etaCalculation?.duration.max || 0,
      delivery_fee: this.orderStore.fee.shippingPounds || 0,
      threshold: this.orderStore.fee.thresholdPounds || 0,
      is_surger: this.orderStore.etaCalculation?.highDemand || false,
    });

    analyticsService.track(AnalyticsEvent.PIXEL_ADD_TO_CART, {
      content_ids: [cartItem.id],
      content_type: 'product',
      currency: company.currency,
      value: this.mainStore.toFloat(cartItem.priceFormatted),
    });

    const firebaseEvent =
      action === 'add'
        ? AnalyticsEvent.ADD_TO_CART
        : AnalyticsEvent.REMOVE_FROM_CART;
    analyticsService.track(firebaseEvent, {
      currency: this.orderStore.currency.toUpperCase(),
      items: [
        {
          item_id: isBundle
            ? cartItem.id
            : (cartItem as Offer).sku || cartItem.id,
          item_name: cartItem.name,
          quantity: 1,
          promotion_id: '',
          promotion_name: '',
          affiliation: '',
          coupon: this.promocode.value,
          creative_name: '',
          creative_slot: '',
          discount: cartItem.discountPrice
            ? this.mainStore.toFloat(
                this.mainStore.convertPenceToPounds(
                  cartItem.price - cartItem.discountPrice,
                ),
              )
            : 0,
          index: 0,
          item_brand: '',
          item_category: cartItem.categoryName,
          item_list_name: cartItem.categoryName,
          item_list_id: cartItem.categoryId,
          item_variant: '',
          location_id: '',
          tax: 0,
          price: this.mainStore.toFloat(cartItem.priceFormatted),
          currency: this.orderStore.currency.toUpperCase(),
        },
      ],
      value: this.mainStore.toFloat(cartItem.priceFormatted),
    });
  }

  private getEffectiveItemPrice(item: CartItem): number {
    if (isProductBundle(item)) {
      return (item as CartItemBundle).price;
    }
    const multibuyLevel = getApplicableMultibuyLevel(
      (item as CartItemOffer).multibuyPriceLevels,
      item.count,
    );
    if (multibuyLevel) return multibuyLevel.unitPrice;
    return (item as CartItemOffer).discountPrice || item.price;
  }

  private leftUntilFreeFee(
    totalPrice: number,
    feeThreshold: number,
  ): [string, string] {
    const zero: [string, string] = ['0', '100%'];
    if (!this.cart.length) {
      return zero;
    }

    if (totalPrice >= feeThreshold) {
      return zero;
    }
    const remainder = feeThreshold - totalPrice;
    const remainderPercent = 100 - (remainder * 100) / (feeThreshold || 1);
    return [
      this.mainStore.convertPenceToPounds(remainder),
      remainderPercent + '%',
    ];
  }

  /**
   * One-time migration: move cart data from old CatalogStore localStorage key
   * to the new CartStore key. Runs before makePersistable so hydration reads
   * the migrated data.
   */
  private migrateFromCatalogStore(): void {
    if (typeof window === 'undefined') return;

    try {
      const cartStoreRaw = window.localStorage.getItem('CartStore');
      if (cartStoreRaw) {
        const cartStoreData = JSON.parse(cartStoreRaw);
        if (cartStoreData.cart?.length) return;
      }

      const catalogRaw = window.localStorage.getItem('CatalogStore');
      if (!catalogRaw) return;

      const catalogData = JSON.parse(catalogRaw);
      if (!catalogData.cart?.length) return;

      window.localStorage.setItem(
        'CartStore',
        JSON.stringify({ cart: catalogData.cart }),
      );

      delete catalogData.cart;
      window.localStorage.setItem('CatalogStore', JSON.stringify(catalogData));
    } catch {
      // Silent fail — user starts with empty cart
    }
  }

  private get cartHash(): string {
    return JSON.stringify(
      this.selectedCartItems.map(({ sku, count }) => ({ sku, count })),
    );
  }

  private get calculateDepsHash(): string {
    const deliveryMethod = this.checkoutStore?.deliveryMethod;
    // Read warehouse straight from etaCalculation (not via etaWarehouseCode
    // getter) so cache key and buildCalculateRequest payload reference the
    // exact same field of the same object — prevents a stale default from
    // the getter ending up in either side.
    const etaCalculation = this.orderStore?.etaCalculation;
    return JSON.stringify({
      warehouse: etaCalculation?.warehouse.code,
      zone: deliveryMethod
        ? etaCalculation?.warehouse.zone[deliveryMethod]?.id
        : undefined,
      deliveryMethod,
      address: {
        lat: this.userStore?.deliveryAddress?.coordinates.lat,
        lng: this.userStore?.deliveryAddress?.coordinates.lng,
      },
      promocode: this.promotionStore?.promocode?.coupon?.code,
      useLoyaltyPoints: this.userStore?.loyaltyPointsStore?.useLoyaltyPoints,
      tips: this.orderStore?.orderTipsValue,
      deliveries: this.checkoutStore?.deliveries?.map((d) => ({
        slot: d.slot?.schedule_slot_id,
        items: d.itemsIds,
      })),
    });
  }

  private async executeCalculate(): Promise<CartCalculationResponse> {
    const requestData = this.buildCalculateRequest();

    Sentry.withScope((scope) => {
      scope.setExtras({ context: 'Calculate', data: requestData });
      Sentry.captureMessage('[Checkout] CALCULATE REQUEST DATA', 'warning');
    });

    try {
      const response = await CatalogRequests.calculateCart(requestData);

      Sentry.withScope((scope) => {
        scope.setExtras({ context: 'Calculate', data: response });
        Sentry.captureMessage('[Checkout] CALCULATE RESPONSE DATA', 'warning');
      });

      return response;
    } catch (error) {
      runInAction(() => {
        this.handleCalculateError(error);
      });
      throw error;
    }
  }

  private buildCalculateRequest(): CartCalculationRequest {
    const selectedItems = this.selectedCartItems.map((item) => toJS(item));

    const deliveries =
      this.checkoutStore.deliveries?.map(({ itemsIds, slot }) => {
        const requestedItems: CartCalculationItemRequest[] = selectedItems
          .filter(
            (item) => itemsIds.includes(item.sku) && !isProductBundle(item),
          )
          .map((item) => ({
            sku: (item as Offer).sku,
            requested_quantity: item.count,
          }));

        const requestedBundles: CartBundlesCalculationItemRequest[] =
          selectedItems
            .filter(
              (item) => itemsIds.includes(item.sku) && isProductBundle(item),
            )
            .map((item) => ({
              id: item.id,
              requested_quantity: item.count,
            }));

        return {
          bundles: requestedBundles,
          items: requestedItems,
          slot_delivery_details: slot && {
            schedule_slot_id: slot.schedule_slot_id,
            current_date: slot.current_date,
          },
        };
      }) ?? [];

    // Read warehouse_code and zone_id from the SAME etaCalculation snapshot
    // so they cannot drift apart. Using the etaWarehouseCode getter here
    // was unsafe: its `deliveryAddress && etaCalculation.warehouse.code`
    // check falls back to company.config.warehouse.default whenever
    // deliveryAddress is momentarily falsy, while zone?.id continues to
    // read from etaCalculation.warehouse.zone — producing a 422
    // "Delivery not available" with mismatched warehouse/zone.
    const etaCalculation = this.orderStore.etaCalculation;
    const zone =
      etaCalculation?.warehouse.zone[this.checkoutStore.deliveryMethod];
    const { deliveryAddress, loyaltyPointsStore } = this.userStore;

    const requestData: CartCalculationRequest = {
      address: {
        latitude:
          deliveryAddress?.coordinates.lat ?? company.config.map.center.lat,
        longitude:
          deliveryAddress?.coordinates.lng ?? company.config.map.center.lng,
      },
      deliveries,
      seller: 'jiffy',
      should_use_loyalty_points:
        this.promocode.coupon?.code && !loyaltyPointsStore.workWithPromocode
          ? false
          : loyaltyPointsStore.useLoyaltyPoints,
      should_use_bonuses: false,
      warehouse_code:
        etaCalculation?.warehouse.code || company.config.warehouse.default,
      zone_id: zone?.id,
      promocode:
        loyaltyPointsStore.useLoyaltyPoints &&
        !loyaltyPointsStore.workWithPromocode
          ? undefined
          : this.promocode.coupon?.code,
      tips_amount: this.mainStore.convertPoundsToPence(
        this.orderStore.orderTipsValue,
      ),
    };

    if (this.orderStore.deliveryStore.version === DeliveryServiceVersion.ONE) {
      requestData.delivery_method = this.checkoutStore.deliveryMethod;
    } else {
      const deliveryMethod = LEGACY_TO_NEW_DELIVERY_METHOD_NAME[
        this.checkoutStore.deliveryMethod
      ] as DeliveryMethods;
      const mechanic =
        this.checkoutStore.deliveryMethod ===
        ETADeliveryMethodType.ClickAndCollect
          ? DeliveryMechanic.PICKUP
          : deliveries.some((item) => !!item.slot_delivery_details)
            ? DeliveryMechanic.SCHEDULED
            : DeliveryMechanic.ON_DEMAND;

      const deliveryProvider =
        this.orderStore.deliveryStore.deliveryProviderByMethod[
          deliveryMethod
        ] || undefined;

      requestData.delivery_provider = deliveryProvider;
      requestData.delivery_method = deliveryMethod;
      requestData.delivery_mechanic = mechanic;
    }

    return requestData;
  }

  private processCalculateResponse(response: CartCalculationResponse): void {
    const {
      base_total: totalBasePence = 0,
      discount_total: totalDiscountPence = 0,
      paid_total: paidPence,
      promocode_discount: promocodeDiscountPence = 0,
      paid_bonuses = 0,
      paid_loyalty_points = 0,
      tax_percent: taxPercent,
      tax_amount: taxAmount,
      orders,
      minimal_order_value_error,
    } = response;

    if (!orders[0]) {
      throw new Error('Something went wrong');
    }

    const { promocode, promocodeError, promocodeCalculationNote } = orders[0];

    this.mainStore.sendToRN('sendTags', {
      cart_price: this.mainStore.toFloat(paidPence),
    });

    const amountLeftToDiscount = this.calculateAmountLeftToDiscount(
      totalBasePence,
      totalDiscountPence,
    );

    this.deliveriesOrders = orders;
    this.totalCartPrice = {
      amountLeftToDiscount,
      base: this.mainStore.convertPenceToPounds(totalBasePence),
      discount: this.mainStore.convertPenceToPounds(totalDiscountPence),
      paidWithDiscount: this.mainStore.convertPenceToPounds(
        totalBasePence -
          (totalDiscountPence +
            promocodeDiscountPence +
            paid_bonuses +
            paid_loyalty_points),
      ),
      paidWithoutDiscount: this.mainStore.convertPenceToPounds(
        totalBasePence - (totalDiscountPence + paid_bonuses),
      ),
      paid: this.mainStore.convertPenceToPounds(
        paidPence - paid_loyalty_points,
      ),
      promocodeDiscount: this.mainStore.convertPenceToPounds(
        promocodeDiscountPence,
      ),
      loyaltyPointsDiscount:
        this.mainStore.convertPenceToPounds(paid_loyalty_points),
      paidBonusesPence: paid_bonuses,
      paidLoyaltyPointsPence: paid_loyalty_points,
      taxPercent,
      taxAmount,
      minimal_order_value_error,
    };

    if (this.cart.length) {
      const slugsList = this.cart.reduce<Record<string, string>>(
        (acc, item) => {
          if (!item.slug) {
            return acc;
          }
          acc[item.sku] = item.slug;
          return acc;
        },
        {},
      );

      this.updateCartItems(
        this.selectedCartItems
          .filter((item) => !isProductBundle(item))
          .map((item) => item.sku),
        this.buildCartObj(),
        orders
          .flatMap(({ items }) => items)
          .map((item) => {
            item.slug = slugsList[item.sku];
            return item;
          }),
      );
    }

    if (promocode) {
      this.promotionStore.handleCalculateWithPromocodeResponse(
        promocodeError,
        promocodeCalculationNote,
      );
    }

    if (!promocode && this.promotionStore.applyPromocodeMeta.isLoading) {
      this.promotionStore.applyPromocodeMeta.setLoading(false);
    }
  }

  private handleCalculateError(error: unknown): void {
    const cartObj = this.buildCartObj();

    if (error instanceof AxiosError && error.response?.data?.data?.length) {
      this.changeCountProductList = [];
      for (let i = 0; i < error.response.data.data.length; i++) {
        if (error.response.data.data[i].on_stock <= 0) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          cartObj[error.response.data.data[i].sku].count = 0;
        } else {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          cartObj[error.response.data.data[i].sku].count =
            error.response.data.data[i].on_stock;
          this.changeCountProductList.push(
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            toJS(cartObj[error.response.data.data[i].sku]),
          );
        }
      }
      this.cart = Object.values(cartObj);
    }

    if (
      error instanceof AxiosError &&
      error?.response?.data?.data?.error_code === 'MISSING_BIRTHDAY'
    ) {
      this.isMissingBirthdayErrorCatched = true;
    }

    if (
      error instanceof AxiosError &&
      error?.response?.data?.data?.data?.items?.length
    ) {
      const { response } = error;
      const errorResponse: CalculateStockErrorInstance = {
        isAgeRestriction:
          response?.data?.data?.error_code === 'AGE_RESTRICTION',
        items: [],
        bundles: null,
        ...response?.data?.data?.data,
      };

      this.cartStockChangesStore.openNotifierModal(errorResponse);
      this.changeCountProductList.push(
        ...(errorResponse.items
          .map((item) => cartObj[item.sku])
          .filter(Boolean) as CartItem[]),
      );
    }

    if (this.promotionStore.applyPromocodeMeta.isLoading) {
      this.promocode.success = false;
      this.promocode.errorType = 'error';
      this.promocode.message = i18n.t('errors:promocodeError');

      this.promotionStore.applyPromocodeMeta.setLoading(false);
    }

    if (
      error instanceof AxiosError &&
      error.response?.status === 500 &&
      error.response?.data?.errors?.includes('Calculate Cart Api Error')
    ) {
      this.orderStore.reCalculateDeliveries();
    }
  }

  private buildCartObj(): Record<string, CartItem> {
    const cartObj: Record<string, CartItem> = {};
    this.selectedCartItems.forEach((item) => {
      const key = isProductBundle(item) ? `${item.id}` : item.sku;
      cartObj[key] = toJS(item);
    });
    return cartObj;
  }

  private calculateAmountLeftToDiscount(
    baseTotalPence: number,
    discountTotalPence: number,
  ): string {
    const totalDiscountPence = baseTotalPence - discountTotalPence;

    if (!this.promocode.success || !this.promocode.coupon) {
      return '0';
    }

    const {
      type,
      minimumPurchase,
      value: discountValuePence,
    } = this.promocode.coupon;

    if (totalDiscountPence < minimumPurchase) {
      const result = minimumPurchase - totalDiscountPence;
      return result < 0 ? '0' : this.mainStore.convertPenceToPounds(result);
    }

    if (type === 'FIXED' && totalDiscountPence < discountValuePence) {
      const result = discountValuePence - totalDiscountPence;
      return result < 0 ? '0' : this.mainStore.convertPenceToPounds(result);
    }

    return '0';
  }

  private updateCartItems(
    skuList: string[],
    cartObj: Record<string, CartItem>,
    items: CartCalculationItemResponse[],
  ): boolean {
    this.changePriceProductList = [];
    this.changeCountProductList = [];

    items.forEach((item) => {
      if (item.is_gift) {
        return;
      }
      const cartItem = cartObj[item.sku];
      if (!cartItem) {
        return;
      }

      const originCartItem = toJS(cartItem);
      let isPriceChanged = false;

      skuList = skuList.filter((sku) => sku !== item.sku);

      if (item.sellable !== undefined) {
        cartItem.sellable = item.sellable;

        const warehouseCode = this.orderStore.etaCalculation?.warehouse.code;
        if (warehouseCode) {
          (cartItem as Mutable<CartItem>).sellableWarehouses = {
            ...cartItem.sellableWarehouses,
            [warehouseCode]: String(item.sellable),
          };
        }
      }

      const paidPrice =
        item.discount_price !== undefined
          ? item.discount_price
          : item.paid_price;

      if (item.discount_amount && cartItem.discountPrice !== paidPrice) {
        const oldDiscountPrice = cartItem.discountPrice;
        cartItem.discountPrice = paidPrice;
        if (paidPrice > oldDiscountPrice) {
          isPriceChanged = true;
        }
      }

      if (!item.discount_amount && cartItem.discountPrice) {
        cartItem.discountPrice = 0;
        isPriceChanged = true;
      }

      if (cartItem.price !== item.base_price) {
        const oldPrice = cartItem.price;
        cartItem.price = item.base_price;
        if (item.base_price > oldPrice) {
          isPriceChanged = true;
        }
      }

      if (isPriceChanged) {
        this.changePriceProductList.push(originCartItem);
      }
    });

    skuList.forEach((sku) => {
      const cartItem = cartObj[sku];
      if (!cartItem) {
        return;
      }
      cartItem.count = 0;
    });

    const cartItems = Object.values(cartObj);
    if (this.cart.length === cartItems.length) {
      this.cart = cartItems.map(calcProductOfferFields) as CartItem[];
    } else {
      this.cart = this.cart.map((cartItem) => {
        const updatedItem = cartItems.find(({ sku }) => sku === cartItem.sku);
        return updatedItem ?? cartItem;
      });
    }

    return !!(
      this.changePriceProductList.length || this.changeCountProductList.length
    );
  }
}
